import { doc, onSnapshot, Timestamp, type Unsubscribe } from "firebase/firestore";
import { db } from "./firebaseConfig";
import { login, logout, watchAuthState } from "./auth";
import { requestLocation } from "./requestLocation";
import { LocationMap, type LocationMarkerData } from "./map";
import type { User } from "firebase/auth";

const TIMEOUT_MS = 20_000;

type RequestStatus = "pending" | "fulfilled" | "failed" | "timeout";

interface LocationRequestDoc {
  requestedBy?: string;
  requestedAt?: Timestamp;
  targetUid?: string;
  requestId?: string;
  status?: "pending" | "fulfilled" | "failed";
  fulfilledAt?: Timestamp | null;
}

interface CurrentLocationDoc {
  lat?: number;
  lng?: number;
  accuracyMeters?: number;
  capturedAt?: Timestamp;
  uploadedBy?: string;
  requestId?: string;
}

const appEl = document.getElementById("app");
if (!appEl) {
  throw new Error("#app element not found");
}

// ------- アプリ全体の状態 -------
let activeRequestId: string | null = null;
let timeoutTimer: number | null = null;
let locationRequestUnsub: Unsubscribe | null = null;
let currentLocationUnsub: Unsubscribe | null = null;
let leafletMap: LocationMap | null = null;

function clearTimeoutTimer(): void {
  if (timeoutTimer !== null) {
    window.clearTimeout(timeoutTimer);
    timeoutTimer = null;
  }
}

function teardownSubscriptions(): void {
  clearTimeoutTimer();
  if (locationRequestUnsub) {
    locationRequestUnsub();
    locationRequestUnsub = null;
  }
  if (currentLocationUnsub) {
    currentLocationUnsub();
    currentLocationUnsub = null;
  }
  activeRequestId = null;
  leafletMap = null;
}

// ------- 画面描画 -------

function renderLoginScreen(): void {
  teardownSubscriptions();
  if (!appEl) return;

  appEl.innerHTML = `
    <div class="login-screen">
      <div class="login-card">
        <h1>ふたりの位置情報共有</h1>
        <form id="login-form">
          <div class="field">
            <label for="email">メールアドレス</label>
            <input id="email" name="email" type="email" autocomplete="username" required />
          </div>
          <div class="field">
            <label for="password">パスワード</label>
            <input id="password" name="password" type="password" autocomplete="current-password" required />
          </div>
          <button type="submit" class="btn-primary" id="login-btn">ログイン</button>
        </form>
        <div class="error-text" id="login-error"></div>
      </div>
    </div>
  `;

  const form = document.getElementById("login-form") as HTMLFormElement;
  const emailInput = document.getElementById("email") as HTMLInputElement;
  const passwordInput = document.getElementById("password") as HTMLInputElement;
  const loginBtn = document.getElementById("login-btn") as HTMLButtonElement;
  const errorEl = document.getElementById("login-error") as HTMLDivElement;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.textContent = "";
    loginBtn.disabled = true;
    loginBtn.textContent = "ログイン中...";
    try {
      await login(emailInput.value, passwordInput.value);
      // 成功後の画面遷移は watchAuthState のコールバックで行われる。
    } catch {
      errorEl.textContent = "ログインに失敗しました。メールアドレスとパスワードを確認してください。";
      loginBtn.disabled = false;
      loginBtn.textContent = "ログイン";
    }
  });
}

function renderMainScreen(user: User): void {
  if (!appEl) return;

  appEl.innerHTML = `
    <div class="main-screen">
      <div class="top-bar">
        <span class="user-email">${escapeHtml(user.email ?? "")}</span>
        <button class="btn-logout" id="logout-btn">ログアウト</button>
      </div>
      <div id="map"></div>
      <div class="control-panel">
        <button class="btn-request" id="request-btn">現在地をリクエスト</button>
        <div class="status-line" id="status-line"></div>
        <div class="location-meta" id="location-meta"></div>
      </div>
    </div>
  `;

  leafletMap = new LocationMap("map");
  // レイアウト確定後にLeafletへ再計算させる(iOS Safariでのアドレスバー変動対策)。
  window.setTimeout(() => leafletMap?.invalidateSize(), 0);

  const requestBtn = document.getElementById("request-btn") as HTMLButtonElement;
  const statusLine = document.getElementById("status-line") as HTMLDivElement;
  const logoutBtn = document.getElementById("logout-btn") as HTMLButtonElement;

  logoutBtn.addEventListener("click", async () => {
    await logout();
  });

  requestBtn.addEventListener("click", async () => {
    requestBtn.disabled = true;
    setStatus(statusLine, "pending", "リクエスト送信中...");

    const result = await requestLocation();

    if (!result.ok) {
      requestBtn.disabled = false;
      setStatus(statusLine, "failed", `リクエストに失敗しました (${result.error})`);
      return;
    }

    activeRequestId = result.requestId;
    setStatus(statusLine, "pending", "位置情報取得中...");
    startTimeoutTimer(statusLine, requestBtn);
  });

  subscribeLocationRequest(statusLine, requestBtn);
  subscribeCurrentLocation();
}

function setStatus(el: HTMLDivElement, status: RequestStatus, text: string): void {
  el.className = `status-line ${status}`;
  el.textContent = text;
}

function startTimeoutTimer(statusLine: HTMLDivElement, requestBtn: HTMLButtonElement): void {
  clearTimeoutTimer();
  const requestIdAtStart = activeRequestId;
  timeoutTimer = window.setTimeout(() => {
    // 20秒経過時点でもまだ同じリクエストがpendingのままならタイムアウト表示にする。
    // Firestoreへの書き込みは行わない(architecture.md 5節: READ-ONLY判定でよい)。
    if (activeRequestId === requestIdAtStart) {
      setStatus(statusLine, "timeout", "タイムアウトしました。もう一度お試しください。");
      requestBtn.disabled = false;
    }
  }, TIMEOUT_MS);
}

function subscribeLocationRequest(statusLine: HTMLDivElement, requestBtn: HTMLButtonElement): void {
  const ref = doc(db, "locationRequests", "current");
  locationRequestUnsub = onSnapshot(
    ref,
    (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data() as LocationRequestDoc;

      // 自分が発行したリクエストと一致しない場合は無視する(相手が発行したリクエスト等)。
      if (!activeRequestId || data.requestId !== activeRequestId) return;

      if (data.status === "fulfilled") {
        clearTimeoutTimer();
        setStatus(statusLine, "fulfilled", "位置情報を取得しました。");
        requestBtn.disabled = false;
      } else if (data.status === "failed") {
        clearTimeoutTimer();
        setStatus(statusLine, "failed", "位置情報の取得に失敗しました。");
        requestBtn.disabled = false;
      }
      // status === "pending" の場合はタイムアウトタイマー任せにする。
    },
    (err) => {
      setStatus(statusLine, "failed", `購読エラー: ${err.message}`);
    },
  );
}

function subscribeCurrentLocation(): void {
  const ref = doc(db, "currentLocation", "latest");
  currentLocationUnsub = onSnapshot(
    ref,
    (snapshot) => {
      if (!snapshot.exists() || !leafletMap) return;
      const data = snapshot.data() as CurrentLocationDoc;

      if (
        typeof data.lat !== "number" ||
        typeof data.lng !== "number" ||
        typeof data.accuracyMeters !== "number"
      ) {
        return;
      }

      const markerData: LocationMarkerData = {
        lat: data.lat,
        lng: data.lng,
        accuracyMeters: data.accuracyMeters,
        capturedAt: data.capturedAt ? data.capturedAt.toDate() : null,
      };
      leafletMap.update(markerData);
      updateLocationMeta(markerData);
    },
    () => {
      // 現在地購読エラーはステータス行を上書きしたくないため、metaにのみ表示する。
      const metaEl = document.getElementById("location-meta");
      if (metaEl) metaEl.textContent = "現在地の購読でエラーが発生しました。";
    },
  );
}

function updateLocationMeta(data: LocationMarkerData): void {
  const metaEl = document.getElementById("location-meta");
  if (!metaEl) return;
  const capturedText = data.capturedAt ? data.capturedAt.toLocaleString("ja-JP") : "不明";
  metaEl.textContent = `最終更新: ${capturedText} / 精度: 約${Math.round(data.accuracyMeters)}m`;
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

// ------- 画面切り替えのエントリポイント -------
watchAuthState((user) => {
  if (user) {
    renderMainScreen(user);
  } else {
    renderLoginScreen();
  }
});
